// src/context/ActivityFeedContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { classifyKindFromVariant } from "../utils/storagePaths";
import { pLimit } from "../utils/pLimit";

import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit as fsLimit,
  onSnapshot,
} from "firebase/firestore";

// ---- processing thresholds ----
const PROCESSING_DELAY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Decide if the Firestore doc already has "results".
 * Tweak this as your pipeline evolves. For now we treat any of these fields
 * as proof that results landed.
 */
function hasResultFields(data) {
  return Boolean(
    data?.best_traces ||
      data?.rr_count ||
      data?.rr_median_ipi ||
      data?.estimated_rr_bpm
  );
}

/**
 * Compute a lightweight status + flags for UI.
 */
function computeStatusAndFlags({ kind, tsMs, data, nowMs }) {
  const resultsPresent = hasResultFields(data);
  const age = Math.max(0, nowMs - (tsMs || 0));
  // Default assumptions
  let status = resultsPresent ? "ready" : "indexed";
  const flags = { delayed: false, failed: false };

  if (data?.status == "failed") {
    status = "failed";
    flags.failed = true;
  }

  if (!resultsPresent && status !== "failed") {
    status = "processing";
    // Only spirometry gets a "delayed" flag for now per your ask
    if (kind === "spirometry" && age > PROCESSING_DELAY_MS) {
      status = "delayed";
      flags.delayed = true;
    }
  }

  return { status, flags };
}

const ActivityFeedContext = createContext(null);

/**
 * ActivityFeedProvider (Firestore-backed)
 *
 * Reads index docs from:
 *   measurements/{uid}/{tag}/{filenameBase}
 * ordered by ts desc, and builds feed items.
 *
 * Props:
 *  - storage: Firebase Storage (kept for future getDownloadURL needs)
 *  - uid: user id
 *  - tag: first-segment tag used in Firestore (e.g. "breatheultra")
 *  - pollMs: (unused now, kept for API compatibility)
 *  - debug: enable console timings
 */
export function ActivityFeedProvider({
  uid,
  tag = "breatheultra",
  children,
  debug = false,
}) {
  // -- logging helpers --------------------------------------------------------
  const logPrefix = "[ActivityFeed]";
  const LOG = (...args) => debug && console.log(logPrefix, ...args);
  const GROUP = (label) =>
    debug && console.groupCollapsed(`${logPrefix} ${label}`);
  const GROUP_END = () => debug && console.groupEnd();
  const T = {
    start(label) {
      if (!debug) return;
      console.time(`${logPrefix} ${label}`);
    },
    end(label) {
      if (!debug) return;
      console.timeEnd(`${logPrefix} ${label}`);
    },
  };
  // ---------------------------------------------------------------------------

  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const unsubRef = useRef(null);

  // In-memory caches (you can keep them if you later lazy-load JSON bodies)
  const metaCacheRef = useRef(new Map());
  const limit = useMemo(
    () => pLimit(navigator.connection?.effectiveType ? 8 : 12),
    []
  );

  const db = useMemo(() => getFirestore(), []);

  function contentSignature(d = {}) {
    // Only the fields that matter for your previews & charts
    const sig = [
      Array.isArray(d?.best_traces?.[0]) ? d.best_traces[0].length : 0,
      Array.isArray(d?.trace) ? d.trace.length : 0,
      Array.isArray(d?.times) ? d.times.length : 0,
      Number.isFinite(d?.rr_median_ipi) ? d.rr_median_ipi : "",
      Number.isFinite(d?.estimated_rr_bpm) ? d.estimated_rr_bpm : "",
      Number.isFinite(d?.lung_age_years) ? d.lung_age_years : "",
      Number.isFinite(d?.ratio) ? d.ratio : "",
      d?.status || "",
    ].join("|");
    return sig;
  }

  // Build items from cache for consistency with your UI shape
  const buildItemsFromCache = useCallback(() => {
    const items = Array.from(metaCacheRef.current.values())
      .map((cached) => {
        if (!cached) return null;
        const {
          timestampMs,
          date,
          variant,
          kind,
          repo,
          metadataUrl, // may be null (resolve on demand)
          raw, // may be null until you open the detail view
          sig,
          baseName,
          uid,
          _status,
          _flags,
        } = cached;

        const status = _status || "indexed";
        const flags = _flags || {};

        return {
          uid,
          timestamp: timestampMs || 0,
          date,
          variant,
          kind,
          repo,
          status,
          flags,
          metadataUrl,
          metadata: raw,
          sig, // <— revision for consumers that want to re-run effects
          baseName,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return items;
  }, []);

  // Subscribe to Firestore index
  const subscribeOnce = useCallback(() => {
    if (!uid || !tag) {
      setState({ loading: false, error: null, items: [] });
      return;
    }

    // Firestore path: measurements/{uid}/{tag}
    const colRef = collection(db, "measurements", uid, tag);
    const q = query(colRef, orderBy("ts", "desc"), fsLimit(200)); // adjust page size if needed

    // clean up previous subscription
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    T.start("onSnapshot");
    unsubRef.current = onSnapshot(
      q,
      (snap) => {
        T.end("onSnapshot");
        const started = performance.now();
        const nowMs = Date.now();
        // Rebuild the in-memory cache from Firestore docs
        // Rebuild the cache each time to avoid key collisions / stale entries
        const nextMap = new Map();
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const baseName = docSnap.id; // filenameBase (unique)
          const tsMs = Number.isFinite(data.ts) ? data.ts : 0;
          const variant = data.variant || "";
          const kind = classifyKindFromVariant(variant);
          const { status: _status, flags: _flags } = computeStatusAndFlags({
            kind,
            tsMs,
            data,
            nowMs,
          });
          nextMap.set(baseName, {
            uid,
            baseName,
            path: data.path || null, // keep if you need Storage
            timestampMs: tsMs,
            date: tsMs ? new Date(tsMs) : undefined,
            variant,
            kind,
            repo: data.repo || null,
            metadataUrl: data.url || null,
            raw: data, // live Firestore doc
            sig: contentSignature(data), // revision stamp
            _status,
            _flags,
          });
        });
        metaCacheRef.current = nextMap;

        const items = buildItemsFromCache();

        setState({ loading: false, error: null, items });

        if (debug) {
          LOG("Firestore index synced", {
            count: items.length,
            elapsedMs: Math.round(performance.now() - started),
            tag,
          });
        }
      },
      (error) => {
        console.error(`${logPrefix} Firestore subscription error`, error);
        setState({ loading: false, error, items: [] });
      }
    );
  }, [db, uid, tag, buildItemsFromCache, debug]);

  // Public API: immediate refresh (kept for compatibility)
  const refreshNow = useCallback(async () => {
    LOG("Manual refresh requested.");
    // With onSnapshot live, there's nothing to do; but keep the function.
    // If you want, you could re-run subscribeOnce() here.
    subscribeOnce();
  }, [subscribeOnce, LOG]);

  useEffect(() => {
    GROUP("Effect init");
    LOG("init", { uid, tag });
    setState((s) => ({ ...s, loading: true }));
    subscribeOnce();

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      GROUP_END();
    };
  }, [uid, tag, subscribeOnce]);

  const value = useMemo(
    () => ({
      ...state, // { loading, error, items }
      refreshNow,
    }),
    [state, refreshNow]
  );

  return (
    <ActivityFeedContext.Provider value={value}>
      {children}
    </ActivityFeedContext.Provider>
  );
}

export function useActivityFeed() {
  const ctx = useContext(ActivityFeedContext);
  if (!ctx)
    throw new Error("useActivityFeed must be used within ActivityFeedProvider");
  return ctx;
}
